import { useMemo } from 'react';
import { useCurrentOrganization } from '../contexts/OrganizationContext';
import type { OrganizationRole } from '../types/organization';
import { ROLE_HIERARCHY } from '../types/organization';

export interface Permissions {
  // Role checks
  isOwner: boolean;
  isAdmin: boolean;
  isMember: boolean;
  isViewer: boolean;
  roleAtLeast: (minRole: OrganizationRole) => boolean;

  // Action permissions
  canManageMembers: boolean;
  canInviteMembers: boolean;
  canUpdateOrg: boolean;
  canDeleteOrg: boolean;
  canChangeRoles: boolean;

  // Member management helpers
  canRemoveMember: (targetRole: OrganizationRole) => boolean;
  canInviteRole: (inviteRole: OrganizationRole) => boolean;
  canChangeRole: (targetRole: OrganizationRole) => boolean;
}

export function usePermissions(): Permissions {
  const { currentRole } = useCurrentOrganization();

  return useMemo(() => {
    const roleAtLeast = (minRole: OrganizationRole): boolean => {
      if (!currentRole) return false;
      return ROLE_HIERARCHY[currentRole] >= ROLE_HIERARCHY[minRole];
    };

    const isOwner = currentRole === 'owner';
    const isAdmin = roleAtLeast('admin');
    const isMember = roleAtLeast('member');
    const isViewer = roleAtLeast('viewer');

    // Action permissions
    const canManageMembers = isAdmin;
    const canInviteMembers = isAdmin;
    const canUpdateOrg = isAdmin;
    const canDeleteOrg = isOwner;
    const canChangeRoles = isOwner;

    // Member management - can only affect roles lower than your own
    const canRemoveMember = (targetRole: OrganizationRole): boolean => {
      if (!currentRole) return false;
      return ROLE_HIERARCHY[currentRole] > ROLE_HIERARCHY[targetRole];
    };

    const canInviteRole = (inviteRole: OrganizationRole): boolean => {
      if (!currentRole) return false;
      // Can only invite roles lower than your own
      return ROLE_HIERARCHY[currentRole] > ROLE_HIERARCHY[inviteRole];
    };

    const canChangeRole = (targetRole: OrganizationRole): boolean => {
      if (!currentRole) return false;
      // Only owners can change roles, and only for lower roles
      return isOwner && ROLE_HIERARCHY[currentRole] > ROLE_HIERARCHY[targetRole];
    };

    return {
      isOwner,
      isAdmin,
      isMember,
      isViewer,
      roleAtLeast,
      canManageMembers,
      canInviteMembers,
      canUpdateOrg,
      canDeleteOrg,
      canChangeRoles,
      canRemoveMember,
      canInviteRole,
      canChangeRole,
    };
  }, [currentRole]);
}

// Convenience hooks for common permission checks
export function useCanManageMembers(): boolean {
  const { canManageMembers } = usePermissions();
  return canManageMembers;
}

export function useCanDeleteOrg(): boolean {
  const { canDeleteOrg } = usePermissions();
  return canDeleteOrg;
}

export function useIsOwner(): boolean {
  const { isOwner } = usePermissions();
  return isOwner;
}

export function useIsAdmin(): boolean {
  const { isAdmin } = usePermissions();
  return isAdmin;
}
